import { createContext, useContext, useState, ReactNode, useMemo } from "react";

import { useDialogSteps } from "ui/dialog";

import { useAvailableStrategies } from "components/modals/add-touchpoints-from-template-modal/hooks/use-available-strategies";

import type { AddTouchpointsFromMondayTemplateContextType } from "components/modals/add-touchpoints-from-template-modal/add-touchpoints-from-template-modal.types";

import { MondayCampaignTemplateDTO } from "generated/schemas";

const AddTouchpointsFromMondayTemplateContext = createContext<
  AddTouchpointsFromMondayTemplateContextType | undefined
>(undefined);

interface AddTouchpointsFromMondayTemplateContextProviderProps {
  children: ReactNode;
  setIsOpen: (isOpen: boolean) => void;
  campaignObjective: string;
  folderId?: string;
  onSuccess: () => void;
}

export const AddTouchpointsFromMondayTemplateContextProvider = ({
  children,
  setIsOpen,
  campaignObjective,
  folderId,
  onSuccess,
}: AddTouchpointsFromMondayTemplateContextProviderProps) => {
  // If folderId is provided, we skip step 1 and go directly to step 2
  const isDirectToStep2 = Boolean(folderId);
  const steps = useDialogSteps({ total: 2, initialStep: isDirectToStep2 ? 2 : 1 });
  const [campaignTemplates, setCampaignTemplates] = useState<MondayCampaignTemplateDTO[]>([]);
  const [isLoadingCampaignTemplates, setIsLoadingCampaignTemplates] = useState(false);
  const [invalidTemplateIds, setInvalidTemplateIds] = useState<Set<string>>(new Set());
  const [usedCampaignTemplateIds, setUsedCampaignTemplateIds] = useState<Set<string>>(new Set());
  const [existingTouchpointNames, setExistingTouchpointNames] = useState<Set<string>>(new Set());

  const { strategies, isLoadingStrategies } = useAvailableStrategies();

  /* `steps`' callbacks use functional updates, so the memo only needs `steps.step`. */
  const value = useMemo(
    () => ({
      ...steps,
      setIsOpen,
      strategies,
      isLoadingStrategies,
      campaignTemplates,
      setCampaignTemplates,
      isLoadingCampaignTemplates,
      setIsLoadingCampaignTemplates,
      invalidTemplateIds,
      setInvalidTemplateIds,
      usedCampaignTemplateIds,
      setUsedCampaignTemplateIds,
      existingTouchpointNames,
      setExistingTouchpointNames,
      campaignObjective,
      folderId,
      onSuccess,
      isDirectToStep2,
    }),
    [
      setIsOpen,
      steps.step,
      strategies,
      isLoadingStrategies,
      campaignTemplates,
      isLoadingCampaignTemplates,
      invalidTemplateIds,
      usedCampaignTemplateIds,
      existingTouchpointNames,
      campaignObjective,
      folderId,
      onSuccess,
      isDirectToStep2,
    ],
  );

  return (
    <AddTouchpointsFromMondayTemplateContext.Provider value={value}>
      {children}
    </AddTouchpointsFromMondayTemplateContext.Provider>
  );
};

export const useAddTouchpointsFromMondayTemplateContext = () => {
  const context = useContext(AddTouchpointsFromMondayTemplateContext);
  if (context === undefined) {
    throw new Error(
      "useAddTouchpointsFromTemplateContext must be used within a AddTouchpointsFromTemplateContextProvider",
    );
  }
  return context;
};
